import { useState } from 'react'
import { Card, Form, Input, Button, Space, Typography, Divider, message } from 'antd'
import { UserOutlined, PhoneOutlined, MailOutlined, TeamOutlined, CloseOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { useCurrentUser } from '@/hooks'
import { userService } from '@/services/userService'
import { errorService } from '@/services/errorService'

const { Title, Text } = Typography

const Profile: React.FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user, isLoading } = useCurrentUser()
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  // Get the previous location from navigation state, default to home
  const previousPath = (location.state as any)?.from || '/'

  const handleClose = () => {
    navigate(previousPath)
  }

  const handleSave = async (values: any) => {
    if (!user) return

    setSaving(true)

    try {
      const result = await userService.updateProfile(
        user.id,
        {
          userName: values.userName,
          phoneNumber: values.phoneNumber
        },
        user.version // Pass current version for optimistic locking
      )

      if (!result.success) {
        errorService.showError(
          'Failed to Update Profile',
          result.message || 'An error occurred while updating your profile',
          result.errors
        )
        return
      }

      message.success('Profile updated successfully')

      // Invalidate and refetch user data to update throughout the app
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      await queryClient.refetchQueries({ queryKey: ['currentUser'] })
    } catch (error: any) {
      errorService.showError(
        'Profile Update Error',
        'An unexpected error occurred while updating your profile',
        [{ code: 'UNKNOWN_ERROR', message: error?.message || String(error) }]
      )
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Card loading={true} style={{ maxWidth: '600px', margin: '0 auto' }}>
        Loading profile...
      </Card>
    )
  }

  if (!user) {
    return (
      <Card style={{ maxWidth: '600px', margin: '0 auto' }}>
        <Text type="danger">Unable to load user profile</Text>
      </Card>
    )
  }

  return (
    <Card style={{ maxWidth: '600px', margin: '0 auto' }} title={<Title level={3}>User Profile</Title>}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          userName: user.userName || user.name,
          phoneNumber: user.phoneNumber || '',
          email: user.email
        }}
      >
        <Form.Item
          label="Display Name"
          name="userName"
          rules={[
            { required: true, message: 'Display name is required' },
            { max: 255, message: 'Display name must be less than 255 characters' }
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Enter your display name"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Phone Number"
          name="phoneNumber"
          rules={[
            { max: 20, message: 'Phone number must be less than 20 characters' }
          ]}
        >
          <Input
            prefix={<PhoneOutlined />}
            placeholder="Enter your phone number (optional)"
            size="large"
          />
        </Form.Item>

        <Divider />

        <Title level={5}>Read-Only Information</Title>

        <Form.Item label="Email">
          <Input
            prefix={<MailOutlined />}
            value={user.email}
            disabled
            size="large"
            style={{ backgroundColor: '#f5f5f5' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Email is managed by your account provider (Cognito) and cannot be changed here
          </Text>
        </Form.Item>

        <Form.Item label="Current Organization">
          <Input
            prefix={<TeamOutlined />}
            value={user.currentOrganization?.name || 'Unknown'}
            disabled
            size="large"
            style={{ backgroundColor: '#f5f5f5' }}
          />
        </Form.Item>

        <Form.Item label="Role in Current Organization">
          <Input
            value={user.currentOrganization?.role || 'Unknown'}
            disabled
            size="large"
            style={{ backgroundColor: '#f5f5f5', textTransform: 'capitalize' }}
          />
        </Form.Item>

        <Divider />

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving} size="large">
              Save Changes
            </Button>
            <Button
              htmlType="button"
              onClick={handleClose}
              size="large"
              icon={<CloseOutlined />}
            >
              Close
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  )
}

export default Profile
